import React, { useState, useEffect, useCallback, useContext } from 'react'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import ApiService from '../../../../services/ApiService'
import {
  BuyOrderExpandedRow,
  BuyOrderExpandedCell,
  BuyOrderPanel,
  BuyOrderPanelHeader,
  BuyOrderPanelTitle,
  BuyOrderForm,
  FormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  FormSelect,
  FormStaticValue,
  BuyOrderActions,
  ActionButton,
  StatusBadge,
  OcBadgeCell,
  CreateOcButton,
  ObservationsRow,
} from './style'

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  DELIVERED: 'Comprada',
  COMPLETED: 'Concluída',
  CANCELLED: 'Cancelada',
}

const UNIT_OPTIONS = ['UN', 'KG', 'MM', 'M']

interface BuyOrderData {
  id: number
  stockInProductionId: number
  quantityRequired: number | null
  quantityToBuy: number | null
  unitPrice: number | null
  totalPrice: number | null
  unitOfMeasurement: string | null
  status: string
  observations: string | null
  createdAt: string
  CreatedBy?: { id: number; name: string }
  isConsumed?: boolean
}

interface BuyOrderSectionProps {
  stockInProductionId: number
  productionId: number
  existingBuyOrder?: BuyOrderData | null
  onBuyOrderChange?: () => void
  colSpan: number
  hideConsumeFromStock?: boolean
}

interface BuyOrderBadgeProps {
  buyOrder?: BuyOrderData | null
  onClick: () => void
}

export const BuyOrderBadge: React.FC<BuyOrderBadgeProps> = ({ buyOrder, onClick }) => {
  if (!buyOrder) {
    return (
      <OcBadgeCell>
        <CreateOcButton type="button" onClick={(e) => { e.stopPropagation(); onClick() }}>
          + OC
        </CreateOcButton>
      </OcBadgeCell>
    )
  }

  return (
    <OcBadgeCell>
      <StatusBadge
        status={buyOrder.status}
        style={{ cursor: 'pointer' }}
        onClick={(e) => { e.stopPropagation(); onClick() }}
      >
        {STATUS_LABELS[buyOrder.status] || buyOrder.status}
      </StatusBadge>
    </OcBadgeCell>
  )
}

const BuyOrderSection: React.FC<BuyOrderSectionProps> = ({
  stockInProductionId,
  productionId,
  existingBuyOrder,
  onBuyOrderChange,
  colSpan,
  hideConsumeFromStock = false,
}) => {
  const [buyOrder, setBuyOrder] = useState<BuyOrderData | null>(existingBuyOrder || null)
  const [isEditing, setIsEditing] = useState(!existingBuyOrder)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isConsuming, setIsConsuming] = useState(false)
  const [isPurchasing, setIsPurchasing] = useState(false)

  const { setFeedback } = useContext(FeedbackContext)
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  })

  const [formData, setFormData] = useState({
    quantityRequired: existingBuyOrder?.quantityRequired?.toString() || '',
    quantityToBuy: existingBuyOrder?.quantityToBuy?.toString() || '',
    unitPrice: existingBuyOrder?.unitPrice?.toString() || '',
    unitOfMeasurement: existingBuyOrder?.unitOfMeasurement || '',
    observations: existingBuyOrder?.observations || '',
    status: existingBuyOrder?.status || 'PENDING',
  })

  useEffect(() => {
    if (existingBuyOrder) {
      setBuyOrder(existingBuyOrder)
      setFormData({
        quantityRequired: existingBuyOrder.quantityRequired?.toString() || '',
        quantityToBuy: existingBuyOrder.quantityToBuy?.toString() || '',
        unitPrice: existingBuyOrder.unitPrice?.toString() || '',
        unitOfMeasurement: existingBuyOrder.unitOfMeasurement || '',
        observations: existingBuyOrder.observations || '',
        status: existingBuyOrder.status || 'PENDING',
      })
    }
  }, [existingBuyOrder])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const calculateTotal = useCallback(() => {
    const qty = parseFloat(formData.quantityToBuy)
    const price = parseFloat(formData.unitPrice)
    if (!isNaN(qty) && !isNaN(price)) {
      return (qty * price).toFixed(2)
    }
    return '-'
  }, [formData.quantityToBuy, formData.unitPrice])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const payload: any = {
        quantityRequired: formData.quantityRequired ? parseFloat(formData.quantityRequired) : undefined,
        quantityToBuy: formData.quantityToBuy ? parseFloat(formData.quantityToBuy) : undefined,
        unitPrice: formData.unitPrice ? parseFloat(formData.unitPrice) : undefined,
        unitOfMeasurement: formData.unitOfMeasurement || undefined,
        observations: formData.observations || undefined,
      }

      if (buyOrder) {
        payload.status = formData.status
        const response = await ApiService.buyOrder.update(buyOrder.id, payload)
        setBuyOrder(response.data)
        setIsEditing(false)
      } else {
        payload.stockInProductionId = stockInProductionId
        const response = await ApiService.buyOrder.create(payload)
        setBuyOrder(response.data)
        setIsEditing(false)
      }
      onBuyOrderChange?.()
      setFeedback({ isOpen: true, message: 'Ordem de compra salva com sucesso!', statusOk: true })
    } catch (error: any) {
      console.error('Erro ao salvar OC:', error)
      setFeedback({ isOpen: true, message: error?.response?.data?.message || 'Erro ao salvar ordem de compra', statusOk: false })
    } finally {
      setIsSaving(false)
    }
  }

  const executeDelete = async () => {
    setIsDeleting(true)
    try {
      await ApiService.buyOrder.delete(buyOrder!.id)
      setBuyOrder(null)
      setFormData({
        quantityRequired: '',
        quantityToBuy: '',
        unitPrice: '',
        unitOfMeasurement: '',
        observations: '',
        status: 'PENDING',
      })
      setIsEditing(true)
      onBuyOrderChange?.()
      setFeedback({ isOpen: true, message: 'Ordem de compra excluída com sucesso!', statusOk: true })
    } catch (error: any) {
      console.error('Erro ao excluir OC:', error)
      setFeedback({ isOpen: true, message: error?.response?.data?.message || 'Erro ao excluir ordem de compra', statusOk: false })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDelete = () => {
    if (!buyOrder) return
    setConfirmModal({
      isOpen: true,
      title: 'Excluir Ordem de Compra',
      message: 'Deseja realmente excluir esta ordem de compra?',
      onConfirm: executeDelete,
    })
  }

  const executeConsumeFromStock = async () => {
    setIsConsuming(true)
    try {
      const response = await ApiService.buyOrder.consumeFromStock(buyOrder!.id)
      setBuyOrder(response.data)
      onBuyOrderChange?.()
      setFeedback({ isOpen: true, message: 'Estoque abatido com sucesso!', statusOk: true })
    } catch (error: any) {
      console.error('Erro ao abater do estoque:', error)
      setFeedback({ isOpen: true, message: error?.response?.data?.message || 'Erro ao abater do estoque', statusOk: false })
    } finally {
      setIsConsuming(false)
    }
  }

  const handleConsumeFromStock = () => {
    if (!buyOrder) return
    setConfirmModal({
      isOpen: true,
      title: 'Abater do Estoque',
      message: 'Confirmar abatimento do estoque? Esta ação irá descontar a quantidade do estoque (na unidade selecionada), criar a movimentação de estoque e concluir esta Ordem de Compra.',
      onConfirm: executeConsumeFromStock,
    })
  }

  const executeMarkPurchased = async () => {
    setIsPurchasing(true)
    try {
      const response = await ApiService.buyOrder.markPurchased(buyOrder!.id)
      setBuyOrder(response.data)
      onBuyOrderChange?.()
      setFeedback({ isOpen: true, message: 'Compra registrada com sucesso!', statusOk: true })
    } catch (error: any) {
      console.error('Erro ao registrar compra:', error)
      setFeedback({ isOpen: true, message: error?.response?.data?.message || 'Erro ao registrar compra', statusOk: false })
    } finally {
      setIsPurchasing(false)
    }
  }

  const handleMarkPurchased = () => {
    if (!buyOrder) return
    setConfirmModal({
      isOpen: true,
      title: 'Confirmar Compra do Item',
      message: 'Confirmar compra do item? Esta ação irá registrar a entrada no estoque e criar a movimentação correspondente. Depois disso, você poderá clicar em "Abater do estoque" para finalizar a OC.',
      onConfirm: executeMarkPurchased,
    })
  }

  const isCompleted = buyOrder?.status === 'COMPLETED'
  const isDelivered = buyOrder?.status === 'DELIVERED'
  const isPending = buyOrder?.status === 'PENDING'
  const anyActionRunning = isConsuming || isPurchasing || isDeleting || isSaving

  return (
    <BuyOrderExpandedRow>
      <BuyOrderExpandedCell colSpan={colSpan}>
        <BuyOrderPanel>
          <BuyOrderPanelHeader>
            <BuyOrderPanelTitle>
              Ordem de Compra
              {buyOrder && (
                <StatusBadge status={buyOrder.status}>
                  {STATUS_LABELS[buyOrder.status] || buyOrder.status}
                </StatusBadge>
              )}
            </BuyOrderPanelTitle>

            {buyOrder && !isEditing && (
              <BuyOrderActions>
                {isPending && (
                  <ActionButton
                    variant="outline"
                    onClick={handleMarkPurchased}
                    disabled={anyActionRunning}
                    title="Registra a entrada no estoque desta quantidade"
                  >
                    {isPurchasing ? 'Registrando...' : 'Item comprado'}
                  </ActionButton>
                )}
                {!buyOrder.isConsumed && (buyOrder.status === 'DELIVERED' || buyOrder.status === 'COMPLETED') && !hideConsumeFromStock && (
                  <ActionButton
                    variant="success"
                    onClick={handleConsumeFromStock}
                    disabled={anyActionRunning}
                    title="Abate a quantidade do estoque e conclui a OC"
                  >
                    {isConsuming ? 'Abatendo...' : 'Abater do estoque'}
                  </ActionButton>
                )}
                {!buyOrder.isConsumed && (
                  <ActionButton
                    variant="ghost"
                    onClick={() => setIsEditing(true)}
                    disabled={anyActionRunning}
                  >
                    Editar
                  </ActionButton>
                )}
                {!buyOrder.isConsumed && (
                  <ActionButton
                    variant="danger"
                    onClick={handleDelete}
                    disabled={anyActionRunning}
                  >
                    {isDeleting ? 'Excluindo...' : 'Excluir'}
                  </ActionButton>
                )}
              </BuyOrderActions>
            )}
          </BuyOrderPanelHeader>

          {isEditing ? (
            <>
              <BuyOrderForm>
                <FormGroup>
                  <FormLabel>Qtd. necessária</FormLabel>
                  <FormInput
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.quantityRequired}
                    onChange={(e) => handleInputChange('quantityRequired', e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Qtd. a comprar</FormLabel>
                  <FormInput
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.quantityToBuy}
                    onChange={(e) => handleInputChange('quantityToBuy', e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Preço unitário</FormLabel>
                  <FormInput
                    type="number"
                    step="0.01"
                    placeholder="R$ 0.00"
                    value={formData.unitPrice}
                    onChange={(e) => handleInputChange('unitPrice', e.target.value)}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Un. medida</FormLabel>
                  <FormSelect
                    value={formData.unitOfMeasurement}
                    onChange={(e) => handleInputChange('unitOfMeasurement', e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    {UNIT_OPTIONS.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </FormSelect>
                </FormGroup>
                <FormGroup>
                  <FormLabel>Total calculado</FormLabel>
                  <FormInput
                    type="text"
                    value={calculateTotal() !== '-' ? `R$ ${calculateTotal()}` : '-'}
                    disabled
                  />
                </FormGroup>
                {buyOrder && (
                  <FormGroup>
                    <FormLabel>Status</FormLabel>
                    <FormSelect
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <option value="PENDING">Pendente</option>
                      <option value="DELIVERED">Comprada</option>
                      <option value="COMPLETED">Concluída</option>
                      <option value="CANCELLED">Cancelada</option>
                    </FormSelect>
                  </FormGroup>
                )}
                <ObservationsRow>
                  <FormGroup>
                    <FormLabel>Observações</FormLabel>
                    <FormTextarea
                      rows={2}
                      placeholder="Observações sobre a ordem de compra..."
                      value={formData.observations}
                      onChange={(e) => handleInputChange('observations', e.target.value)}
                    />
                  </FormGroup>
                </ObservationsRow>
              </BuyOrderForm>

              <BuyOrderActions>
                {buyOrder && (
                  <ActionButton variant="ghost" onClick={() => setIsEditing(false)} disabled={isSaving}>
                    Cancelar
                  </ActionButton>
                )}
                <ActionButton
                  variant="primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Salvando...' : buyOrder ? 'Atualizar OC' : 'Criar OC'}
                </ActionButton>
              </BuyOrderActions>
            </>
          ) : buyOrder ? (
            <BuyOrderForm>
              <FormGroup>
                <FormLabel>Qtd. necessária</FormLabel>
                <FormStaticValue>{buyOrder.quantityRequired ?? '-'}</FormStaticValue>
              </FormGroup>
              <FormGroup>
                <FormLabel>Qtd. a comprar</FormLabel>
                <FormStaticValue>{buyOrder.quantityToBuy ?? '-'}</FormStaticValue>
              </FormGroup>
              <FormGroup>
                <FormLabel>Preço unitário</FormLabel>
                <FormStaticValue>
                  {buyOrder.unitPrice != null ? `R$ ${buyOrder.unitPrice.toFixed(2)}` : '-'}
                </FormStaticValue>
              </FormGroup>
              <FormGroup>
                <FormLabel>Total</FormLabel>
                <FormStaticValue>
                  {buyOrder.totalPrice != null ? `R$ ${buyOrder.totalPrice.toFixed(2)}` : '-'}
                </FormStaticValue>
              </FormGroup>
              <FormGroup>
                <FormLabel>Un. medida</FormLabel>
                <FormStaticValue>{buyOrder.unitOfMeasurement || '-'}</FormStaticValue>
              </FormGroup>
              <FormGroup>
                <FormLabel>Criado por</FormLabel>
                <FormStaticValue>{buyOrder.CreatedBy?.name || '-'}</FormStaticValue>
              </FormGroup>
              {buyOrder.observations && (
                <ObservationsRow>
                  <FormGroup>
                    <FormLabel>Observações</FormLabel>
                    <FormStaticValue style={{ fontWeight: 400, fontSize: 13, color: '#595959' }}>
                      {buyOrder.observations}
                    </FormStaticValue>
                  </FormGroup>
                </ObservationsRow>
              )}
            </BuyOrderForm>
          ) : null}
        </BuyOrderPanel>
      </BuyOrderExpandedCell>
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
            variant: 'outline',
          },
          {
            label: 'Confirmar',
            onClick: () => {
              confirmModal.onConfirm();
              setConfirmModal(prev => ({ ...prev, isOpen: false }));
            },
            variant: 'primary',
          },
        ]}
      />
    </BuyOrderExpandedRow>
  )
}

export default BuyOrderSection
